import { createContext } from 'react';
import { useReducer } from 'react';
import { reducer, initialState } from '../store/create_listing_reducer';
import { AttributeType } from '../../../data/types/attributes';
import { toast } from 'react-toastify';
import { actionSetListingData } from '../store/create_listing_actions';
import { linkTempIds } from './data_formatting_functions_tsx';

export const ProductListingContext = createContext();

//create a provider fun
export const ListingContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  return (
    <ProductListingContext.Provider value={{ state, dispatch }}>
      {children}
    </ProductListingContext.Provider>
  );
};

export const convertDataFormat = (data) => {
  // extract hidden images (images added to attribute or to listing but are marked as not visible)
  const hidden_images = extractHiddenImagesAsImageTypesMap(data);
  const hidden_images_ids = Object.keys(hidden_images);
  const hidden_images_list = Object.values(hidden_images);

  // set images
  data.imagesList = data?.images?.concat(hidden_images_list);
  data.images = data?.images
    ?.map((img) => img?.id)
    ?.concat(hidden_images_ids.map((id) => parseInt(id)));
  data.description_en = data.rich_text_description_en || '';
  data.description_ar = data.rich_text_description_ar || '';
  data.whats_included =
    data.whats_included || data.name_en + ' --- ' + data.name_ar || '';
  data.id_of_draft_to_delete = data.id;
  data.listing_type = data.listing_type_name;
  data.list_on_approval = true;
  data.notToUploadImages = hidden_images_ids.map((id) => parseInt(id));

  return data;
};

const getValuesByType = (attr) => {
  return typeof attr.attr_value_en === 'boolean'
    ? JSON.stringify(attr.attr_value_en)
    : attr.attr_value_en;
};

const getValuesByTypeAr = (attr) => {
  return typeof attr.attr_value_ar === 'boolean'
    ? JSON.stringify(attr.attr_value_ar)
    : attr.attr_value_ar;
};

export const get_all_attributes = async (data, category_attrs) => {
  let attributes = Object.keys(data.official_attributes).map(
    (keyName) => data.official_attributes[keyName],
  ); // Adds official attributes of used category attribute.
  attributes = attributes?.concat(
    Object.keys(category_attrs).map((keyName) => category_attrs[keyName]),
  ); // Adds official attributes of  unused category attribute.
  attributes = attributes?.concat(
    Object.keys(data.custom_attributes).map(
      (keyName) => data.custom_attributes[keyName],
    ),
  ); // Adds customly added attributes, and only used values.
  let all_attributes = [];
  let select_attrs = [];
  let boolean_attrs = [];
  let numeric_attrs = [];
  let seen_attrs = new Set(); // To remove duplicate attributes (in case official attribute is used, it will be added twice (one from official values of data and one from category.attributes), this will prevent that.
  console.log('attributes', attributes);
  attributes?.forEach((attr) => {
    if (attr.attr_id) {
      if (seen_attrs.has(attr.attr_id) || seen_attrs.has(attr.attr_name_en)) {
        // If attribute is numeric, make sure to compare its hard_upper_limit and hard_bottom_limit and take the one with the highest value. This is done here because otherwise it will be skipped in the for-loop.
        if (attr.type === AttributeType.NUMERIC) {
          const existing_attr = all_attributes.find(
            (existing_attr) => existing_attr.id === attr.attr_id,
          );
          if (existing_attr) {
            existing_attr.unit = attr.unit;
            if (
              existing_attr.hard_upper_limit < attr.hard_upper_limit ||
              existing_attr.hard_bottom_limit > attr.hard_bottom_limit
            ) {
              existing_attr.hard_upper_limit = Math.max(
                existing_attr.hard_upper_limit,
                attr.hard_upper_limit,
              );
              existing_attr.hard_bottom_limit = Math.min(
                existing_attr.hard_bottom_limit,
                attr.hard_bottom_limit,
              );
            }
          }
          return;
        }
        return;
      }
      seen_attrs.add(attr.attr_id);
      seen_attrs.add(attr.attr_name_en);
    } else {
      if (seen_attrs.has(attr.id) || seen_attrs.has(attr.name_en)) {
        // If attribute is numeric, make sure to compare its hard_upper_limit and hard_bottom_limit and take the one with the highest value. This is done here because otherwise it will be skipped in the for-loop.
        if (attr.type === AttributeType.NUMERIC) {
          const existing_attr = all_attributes.find(
            (existing_attr) => existing_attr.id === attr.id,
          );
          if (existing_attr) {
            existing_attr.unit = attr.unit;
            if (
              existing_attr.hard_upper_limit < attr.hard_upper_limit ||
              existing_attr.hard_bottom_limit > attr.hard_bottom_limit
            ) {
              existing_attr.hard_upper_limit = Math.max(
                existing_attr.hard_upper_limit,
                attr.hard_upper_limit,
              );
              existing_attr.hard_bottom_limit = Math.min(
                existing_attr.hard_bottom_limit,
                attr.hard_bottom_limit,
              );
            }
          }
          return;
        }
        return;
      }
      seen_attrs.add(attr.id);
      seen_attrs.add(attr.name_en);
    }
    let values = []; // Used for Numeric & Boolean
    let values_en = []; // Used for Select
    let values_ar = []; // Used for Select
    let values_images = []; // Used for Select
    let custom = false;
    let is_variant = false;

    data.attributes.forEach((dataAttr) => {
      if (attr.attr_id === dataAttr.attr_id) {
        custom = dataAttr.is_attribute_custom;
        is_variant = dataAttr.is_variant;
        if (!values_en.includes(getValuesByType(dataAttr))) {
          values_en.push(getValuesByType(dataAttr));
        }
        if (!values_ar.includes(getValuesByTypeAr(dataAttr))) {
          values_ar.push(getValuesByTypeAr(dataAttr));
        }
        if (dataAttr.attr_value_image?.id) {
          if (values_en.length > values_images.length) {
            values_images.push(dataAttr.attr_value_image.id);
          }
        }
        if (
          dataAttr.type === AttributeType.BOOLEAN ||
          dataAttr.type === AttributeType.NUMERIC ||
          dataAttr.attribute_type === AttributeType.BOOLEAN ||
          dataAttr.attribute_type === AttributeType.NUMERIC
        ) {
          if (!values.includes(dataAttr.attr_value_en)) {
            values.push(dataAttr.attr_value_en);
          }
        }
      }
    });

    // Attributs can come in 2 forms, is attr.attribute_full_object (for attributes that are restored through draft/state), and attr directly which is recieved from categories attributes, so we cover both cases.
    if (
      attr?.type === AttributeType.SELECT ||
      attr?.attribute_full_object?.type === AttributeType.SELECT
    ) {
      let en_values = [];
      let ar_values = [];

      attributes.map((i) => {
        const officialValuesEn =
          i?.attribute_full_object?.official_values_en || i?.official_values_en;
        const officialValuesAr =
          i?.attribute_full_object?.official_values_ar || i?.official_values_ar;

        if (officialValuesEn && officialValuesEn.length > 0) {
          en_values.push([i.attr_name_en || i.name_en, officialValuesEn]);
        }

        if (officialValuesAr && officialValuesAr.length > 0) {
          ar_values.push([i.attr_name_ar || i.name_ar, officialValuesAr]);
        }
      });

      all_attributes.push({
        id: attr?.attr_id || attr.id,
        name_en: attr?.attribute_full_object?.name_en || attr.name_en,
        name_ar: attr?.attribute_full_object?.name_ar || attr.name_ar,
        type: attr?.attribute_full_object?.type || attr.type,
        values_en: values_en,
        values_ar: values_ar,
        values_images: values_images,
        is_variable: is_variant,
        custom: custom,
        official_values_en:
          en_values
            .filter(
              ([name, _]) =>
                name ===
                (attr?.name_en || attr?.attribute_full_object?.name_en),
            )
            .flatMap(([, values]) => values) || [],
        official_values_ar:
          ar_values
            .filter(
              ([name, _]) =>
                name ===
                (attr?.name_ar || attr?.attribute_full_object?.name_ar),
            )
            .flatMap(([, values]) => values) || [],
      });
    }
    if (
      attr?.type === AttributeType.NUMERIC ||
      attr?.attribute_full_object?.type === AttributeType.NUMERIC
    ) {
      all_attributes.push({
        id: attr?.attr_id || attr.id,
        name_en: attr?.attribute_full_object?.name_en || attr.name_en,
        name_ar: attr?.attribute_full_object?.name_ar || attr.name_ar,
        type: attr?.attribute_full_object?.type || attr.type,
        values: values,
        is_variable: is_variant,
        unit: attr?.attribute_full_object?.unit || attr.unit,
        hard_bottom_limit:
          attr?.attribute_full_object?.type === 1 || attr.type === 1
            ? attr?.attribute_full_object?.hard_bottom_limit
              ? attr?.attribute_full_object?.hard_bottom_limit
              : attr?.hard_bottom_limit
                ? attr?.hard_bottom_limit
                : Math.max(...values_en)
            : 0, //attr.attribute_full_object.hard_bottom_limit,
        hard_upper_limit:
          attr?.attribute_full_object?.type === 1 || attr.type === 1
            ? attr?.attribute_full_object?.hard_upper_limit
              ? attr?.attribute_full_object?.hard_upper_limit
              : attr?.hard_upper_limit
                ? attr?.hard_upper_limit
                : Math.min(...values_en)
            : 0,
        custom: custom,
      });
    }
    if (
      attr?.type === AttributeType.BOOLEAN ||
      attr?.attribute_full_object?.type === AttributeType.BOOLEAN
    ) {
      all_attributes.push({
        id: attr?.attr_id || attr.id,
        name_en: attr?.attribute_full_object?.name_en || attr.name_en,
        name_ar: attr?.attribute_full_object?.name_ar || attr.name_ar,
        type: attr?.attribute_full_object?.type || attr.type,
        values: values,
        values_images: values_images,
        is_variable: is_variant,
        custom: custom,
      });
    }
  });

  select_attrs = all_attributes.filter(
    (attr) => attr.type === AttributeType.SELECT,
  );
  numeric_attrs = all_attributes.filter(
    (attr) => attr.type === AttributeType.NUMERIC,
  );
  boolean_attrs = all_attributes.filter(
    (attr) => attr.type === AttributeType.BOOLEAN,
  );
  console.log('all_attributes', all_attributes);
  console.log('select_attrs', select_attrs);
  console.log('numeric_attrs', numeric_attrs);
  console.log('boolean_attrs', boolean_attrs);

  //use Float.pareFloat on numeric_attrs to remove extra zeros
  numeric_attrs = numeric_attrs.map((attr) => {
    attr.values = attr.values.map((val) => parseFloat(val).toString());
    return attr;
  });

  // Create Combinations as required
  let combinations = [];
  data.items?.map((comb) => {
    const attr_obj = {};
    comb.attributes.forEach((attr) => {
      if (parseFloat(attr.attr_value)) {
        attr_obj[attr.attr_name_en] = parseFloat(attr.attr_value_en).toString();
      } else {
        attr_obj[attr.attr_name_en] = attr.attr_value_en;
      }
    });
    combinations.push({
      valid: true,
      in_stock: comb.in_stock,
      attrs: { ...attr_obj },
      price: parseFloat(comb.price).toString() || '',
      original_price: parseFloat(comb.original_price).toString() || '',
      image_id: comb.image.id,
      barcode: comb.barcode,
      applied_promotion: comb.applied_promotion,
      ...attr_obj,
    });
  });
  return {
    all_attributes,
    select_attrs,
    boolean_attrs,
    numeric_attrs,
    combinations,
  };
};

function extractHiddenImagesAsImageTypesMap(data) {
  let hidden_images = new Map();
  data?.attributes?.forEach(async (attr) => {
    if (attr.attr_value_image) {
      hidden_images[attr.attr_value_image.id] = attr.attr_value_image;
    }
  });

  // remove images already added to images in data
  data?.images?.forEach((img) => {
    if (img?.id !== undefined && hidden_images[img.id]) {
      delete hidden_images[img.id];
    }
  });

  return hidden_images;
}

export const linkCombinationsToTmpAttributeKey = async (
  state,
  dispatch,
  combinations,
) => {
  const {
    attributesWithTempId,
    numericAttrsWithTempId,
    selectAttrsWithTempId,
    booleanAttrsWithTempId,
  } = await linkTempIds(state, dispatch, false);

  // Link each combination attribute to its temp_attribute_id that was linked to it in attribute_selection_area
  const tmpAttrIdLinkedCombinations =
    combinations?.filter((comb) => comb.valid) || [];
  tmpAttrIdLinkedCombinations?.map((comb) => {
    Object.keys(comb).map((item) => {
      if (comb[item] === 'true') {
        comb[item] = true;
      } else if (comb[item] === 'false') {
        comb[item] = false;
      }
    });
    Object.keys(comb?.attrs || {}).map((key) => {
      if (comb.attrs[key] === 'true') {
        comb.attrs[key] = true;
      } else if (comb.attrs[key] === 'false') {
        comb.attrs[key] = false;
      }
    });
    comb.attribute_temp_id__attribute_value_index = {};
    Object.keys(comb.attrs).map((key) => {
      if (state.all_attributes.length < 1) {
        comb.attribute_temp_id__attribute_value_index = {};
        return;
      }
      const attr = state.all_attributes.find((attr) => attr.name_en === key);
      if (attr) {
        const value =
          attr.type === AttributeType.BOOLEAN
            ? comb.attrs[attr.name_en] === true ||
              comb.attrs[attr.name_en] === 'true'
              ? 1
              : 0
            : attr.type === AttributeType.SELECT
              ? selectAttrsWithTempId
                  .find((select_attr) => select_attr.name_en === attr.name_en)
                  ?.values_en?.findIndex(
                    (value) => value === comb.attrs[attr.name_en],
                  )
              : numericAttrsWithTempId
                  .find((numeric_attr) => numeric_attr.name_en === attr.name_en)
                  ?.values?.findIndex(
                    (value) =>
                      value === comb.attrs[attr.name_en] ||
                      parseFloat(comb.attrs[attr.name_en]) ===
                        parseFloat(value),
                  );
        const temp_attribute_id =
          attr.type === AttributeType.BOOLEAN
            ? booleanAttrsWithTempId.find(
                (bool_attr) => bool_attr.id === attr.id,
              )?.temp_attribute_id
            : attr.type === AttributeType.SELECT
              ? selectAttrsWithTempId.find(
                  (select_attr) => select_attr.id === attr.id,
                )?.temp_attribute_id
              : numericAttrsWithTempId.find(
                  (numeric_attr) => numeric_attr.id === attr.id,
                )?.temp_attribute_id;
        comb.attribute_temp_id__attribute_value_index[temp_attribute_id] =
          value;
      }
      console.group(attr, key);
      console.log('select_attrs', state.data.select_attrs);
      console.log('numeric_attrs', state.data.numeric_attrs);
      console.log('boolean_attrs', state.data.boolean_attrs);
      // console.log('value', value);
      // console.log('temp_attribute_id', temp_attribute_id);
      console.groupEnd();
    });
  });
  dispatch(
    actionSetListingData({
      ...state.data,
      attributes: attributesWithTempId,
      numeric_attrs: numericAttrsWithTempId,
      select_attrs: selectAttrsWithTempId,
      boolean_attrs: booleanAttrsWithTempId,
    }),
  );
  return await toast.promise(
    // wait 3 seconds
    new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          attributesWithTempId,
          numericAttrsWithTempId,
          selectAttrsWithTempId,
          booleanAttrsWithTempId,
          combinations,
        });
      }, 3000);
    }),
    {
      pending: 'Linking attributes & restructuring data, please wait...',
      success: 'Successfully linked',
      error: 'Failed to link temp ids',
    },
  );
};
